import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import React, {FC, useCallback, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {CellValue} from '../../../../../PagePanelComponents/Home/Browse2/CellValue';
import {TailwindContents} from '../../../../../Tailwind';
import {Timestamp} from '../../../../../Timestamp';
import {useEntityProject, useWeaveflowRouteContext} from '../../context';
import {FilterPanel} from '../../filters/FilterPanel';
import {StyledDataGrid} from '../../StyledDataGrid';
import {ColumnInfo} from '../../types';
import {PaginationButtons, RefreshButton} from '../CallsPage/CallsTableButtons';
import {FilterLayoutTemplate} from '../common/SimpleFilterableDataTable';
import {StatusChip} from '../common/StatusChip';
import {useURLSearchParamsDict} from '../util';
import {
  convertISOToDate,
  useThreadsQuery,
} from '../wfReactInterface/tsDataModelHooks';
import {convertFilterModelToDatetimeFilters, extractChatMesage} from './common';
import {ThreadsExportSelector} from './ThreadsExportSelector';
import {ThreadsTableNoRowsOverlay} from './ThreadsTableNoRowsOverlay';

// Convert GridSortModel to backend format
const getSortBy = (gridSort: GridSortModel) => {
  return gridSort.map(sort => {
    return {
      field: sort.field,
      direction: sort.sort ?? 'asc',
    };
  });
};

export const ThreadsTable: FC<{
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (newModel: GridColumnVisibilityModel) => void;

  pinModel: GridPinnedColumnFields;
  setPinModel: (newModel: GridPinnedColumnFields) => void;

  filterModel: GridFilterModel;
  setFilterModel: (newModel: GridFilterModel) => void;

  sortModel: GridSortModel;
  setSortModel: (newModel: GridSortModel) => void;

  paginationModel: GridPaginationModel;
  setPaginationModel: (newModel: GridPaginationModel) => void;
}> = ({
  columnVisibilityModel,
  setColumnVisibilityModel,
  pinModel,
  setPinModel,
  filterModel,
  setFilterModel,
  sortModel,
  setSortModel,
  paginationModel,
  setPaginationModel,
}) => {
  const {entity, project} = useEntityProject();
  const history = useHistory();
  const {peekingRouter} = useWeaveflowRouteContext();

  // Extract selected thread ID from peek parameter
  const {peekPath} = useURLSearchParamsDict();
  const selectedThreadId = useMemo(() => {
    if (!peekPath) return undefined;
    // The peek path format is like: /entity/project/threads/threadId
    const threadMatch = peekPath.match(/\/threads\/([^/?]+)/);
    return threadMatch ? threadMatch[1] : undefined;
  }, [peekPath]);

  // Setup Ref to underlying table
  const apiRef = useGridApiRef();

  // Note: All models are now required props, no need for fallbacks

  // Convert sort model to backend format
  const backendSortBy = useMemo(() => {
    return getSortBy(sortModel);
  }, [sortModel]);

  // Convert filter model to backend datetime filters
  const datetimeFilters = useMemo(() => {
    return convertFilterModelToDatetimeFilters(filterModel);
  }, [filterModel]);

  // Store the query params
  const queryParams = useMemo(() => {
    const effectiveOffset = paginationModel.page * paginationModel.pageSize;
    const effectiveLimit = paginationModel.pageSize;

    const effectiveAfterDatetime = datetimeFilters.after_datetime;
    const effectiveBeforeDatetime = datetimeFilters.before_datetime;

    const filter: any = {
      after_datetime: effectiveAfterDatetime,
    };

    if (effectiveBeforeDatetime) {
      filter.before_datetime = effectiveBeforeDatetime;
    }

    return {
      project_id: `${entity}/${project}`,
      filter,
      sort_by: backendSortBy,
      limit: effectiveLimit,
      offset: effectiveOffset,
    };
  }, [entity, project, backendSortBy, datetimeFilters, paginationModel]);

  // Use the actual threads query hook
  const {threadsState, turnCallsDataResult} = useThreadsQuery(queryParams);

  const loading = threadsState.loading;

  // Create column info for FilterPanel - only include datetime filterable columns
  const filterColumnInfo: ColumnInfo = useMemo(() => {
    return {
      cols: [
        {
          field: 'started_at',
          headerName: 'started_at',
          width: 120,
          type: 'dateTime',
        },
      ],
      colGroupingModel: [],
    };
  }, []);

  // Map server data to table format
  const tableData = useMemo(() => {
    const threads = threadsState.value?.threads || [];
    return threads.map(thread => ({
      id: thread.thread_id,
      thread_id: thread.thread_id,
      status: thread.last_turn_id,
      number_of_traces: thread.turn_count,
      first_turn_id: thread.first_turn_id,
      last_turn_id: thread.last_turn_id,
      start_time: thread.start_time,
      last_updated: thread.last_updated,
      p50_latency: Math.max(thread.p50_turn_duration_ms ?? 0, 0.001),
      p99_latency: Math.max(thread.p99_turn_duration_ms ?? 0, 0.001),
    }));
  }, [threadsState.value]);

  // Refetch function for refresh button
  const refetch = useCallback(() => {
    threadsState.retry();
  }, [threadsState]);

  // Pagination change callback
  const onPaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      if (!setPaginationModel || loading) {
        return;
      }
      setPaginationModel(newModel);
    },
    [loading, setPaginationModel]
  );

  // No rows overlay for empty states
  const noRowsOverlay = useCallback(() => {
    return (
      <ThreadsTableNoRowsOverlay
        threadsLoading={loading}
        threadsResult={tableData}
      />
    );
  }, [loading, tableData]);

  // Define columns for the threads table
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'thread_id',
        headerName: 'Thread ID',
        width: 100,
        flex: 1,
        sortable: false,
        renderCell: params => (
          <div
            className="cursor-pointer truncate font-semibold text-teal-600 hover:text-teal-500"
            onClick={() => {
              const threadId = params.value;
              if (threadId) {
                history.push(
                  peekingRouter.threadUIUrl(entity, project, threadId)
                );
              }
            }}>
            {params.value}
          </div>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        headerAlign: 'center',
        width: 90,
        resizable: false,
        display: 'flex',
        sortable: false,
        renderCell: params => (
          <ThreadStatus
            turnCallsDataResult={turnCallsDataResult}
            lastTurnId={params.value}
          />
        ),
      },
      {
        field: 'number_of_traces',
        headerName: 'Turns',
        width: 80,
        type: 'number',
        sortable: false,
      },
      {
        field: 'first_turn_id',
        headerName: 'First Input',
        width: 320,
        flex: 1,
        sortable: false,
        renderCell: params => (
          <FirstTurnInput
            turnCallsDataResult={turnCallsDataResult}
            turnId={params.value}
          />
        ),
      },
      {
        field: 'last_turn_id',
        headerName: 'Last Output',
        width: 320,
        flex: 1,
        sortable: false,
        renderCell: params => (
          <LastTurnOutput
            turnCallsDataResult={turnCallsDataResult}
            turnId={params.value}
          />
        ),
      },
      {
        field: 'start_time',
        headerName: 'Start Time',
        width: 120,
        minWidth: 100,
        maxWidth: 120,
        sortable: false,
        renderCell: params => {
          if (!params.value) return '';
          const date = convertISOToDate(params.value);
          const value = date.getTime() / 1000;
          return <Timestamp value={value} format="relative" />;
        },
      },
      {
        field: 'last_updated',
        headerName: 'Last Updated',
        width: 120,
        minWidth: 100,
        maxWidth: 120,
        sortable: true,
        sortingOrder: ['desc', 'asc'],
        renderCell: params => {
          if (!params.value) return '';
          const date = convertISOToDate(params.value);
          const value = date.getTime() / 1000;
          return <Timestamp value={value} format="relative" />;
        },
      },
      {
        field: 'p50_latency',
        headerName: 'p50 Latency',
        width: 140,
        type: 'number',
        sortable: false,
        renderCell: params => {
          if (!params.value) return '';
          // Convert from milliseconds to seconds for monthRoundedTime
          const timeS = params.value / 1000;
          return timeS.toFixed(3) + 's';
        },
      },
      {
        field: 'p99_latency',
        headerName: 'p99 Latency',
        width: 140,
        type: 'number',
        sortable: false,
        renderCell: params => {
          if (!params.value) return '';
          // Convert from milliseconds to seconds for monthRoundedTime
          const timeS = params.value / 1000;
          return timeS.toFixed(3) + 's';
        },
      },
    ],
    // Adding the missing peekingRouter to the dependency array causes a re-render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [turnCallsDataResult, entity, history, project]
  );

  return (
    <FilterLayoutTemplate
      filterListSx={{
        pb: 1,
        display: 'flex',
        alignItems: 'center',
      }}
      filterListItems={
        <TailwindContents>
          <RefreshButton onClick={refetch} disabled={loading} />
          <FilterPanel
            entity={entity}
            project={project}
            filterModel={filterModel}
            columnInfo={filterColumnInfo}
            setFilterModel={setFilterModel}
            selectedCalls={[]}
            clearSelectedCalls={() => {}}
            isGrouped={true}
          />
          <div className="ml-auto flex min-w-0 items-center gap-8 overflow-hidden">
            <div className="flex-none">
              <ThreadsExportSelector
                numTotalThreads={tableData.length}
                disabled={tableData.length === 0}
              />
            </div>
          </div>
        </TailwindContents>
      }>
      <StyledDataGrid
        apiRef={apiRef}
        rows={tableData}
        columns={columns}
        loading={loading}
        // Column management
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        pinnedColumns={pinModel}
        onPinnedColumnsChange={setPinModel}
        // Column Menu - disable filter and column management features
        disableColumnFilter={true}
        // Pagination
        pagination
        rowCount={
          // For server pagination without total count, we estimate based on current page
          // If we have a full page, assume there might be more pages
          threadsState.loading
            ? 0
            : (threadsState.value?.threads?.length ?? 0) <
                paginationModel.pageSize
              ? paginationModel.page * paginationModel.pageSize +
                (threadsState.value?.threads?.length ?? 0)
              : (paginationModel.page + 1) * paginationModel.pageSize + 1
        }
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        // Filtering
        filterModel={filterModel}
        filterMode="server"
        // Sorting
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        sortingMode="server"
        // Other settings
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        rowHeight={40}
        columnHeaderHeight={38}
        // Footer settings
        hideFooter={!loading && tableData.length === 0}
        hideFooterSelectedRowCount
        // Row class name for highlighting selected thread
        getRowClassName={params => {
          return params.row.thread_id === selectedThreadId
            ? 'selected-thread-row'
            : '';
        }}
        // Styling
        sx={{
          borderRadius: 0,
          '& .MuiDataGrid-footerContainer': {
            justifyContent: 'flex-start',
          },
          '& .MuiDataGrid-main:focus-visible': {
            outline: 'none',
          },
          // Highlight selected thread row
          '& .selected-thread-row': {
            backgroundColor: 'rgba(169, 237, 242, 0.32)',
            '&:hover': {
              backgroundColor: 'rgba(169, 237, 242, 0.16)',
            },
          },
        }}
        // Custom slots
        slots={{
          pagination: () => <PaginationButtons />,
          loadingOverlay: () => (
            <div className="flex h-full w-full items-center justify-center">
              <WaveLoader size="small" />
            </div>
          ),
          noRowsOverlay,
        }}
        className="tw-style"
      />
    </FilterLayoutTemplate>
  );
};

const TruncatedJsonDisplay: FC<{content: any}> = ({content}) => {
  const message = extractChatMesage(content);
  const jsonString = JSON.stringify(message);
  return <CellValue value={jsonString} />;
};

// Component for displaying thread status based on last turn
const ThreadStatus: FC<{
  turnCallsDataResult: ReturnType<
    typeof useThreadsQuery
  >['turnCallsDataResult'];
  lastTurnId: string;
}> = ({turnCallsDataResult, lastTurnId}) => {
  if (turnCallsDataResult.lastTurnsLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <WaveLoader size="small" />
      </div>
    );
  }

  const status = turnCallsDataResult.lastTurnsStatuses.get(lastTurnId);

  if (!status) {
    return null;
  }

  return (
    <div style={{margin: 'auto'}}>
      <StatusChip value={status} iconOnly />
    </div>
  );
};

interface FirstTurnInputProps {
  turnCallsDataResult: ReturnType<
    typeof useThreadsQuery
  >['turnCallsDataResult'];
  turnId: string;
}

function FirstTurnInput({turnCallsDataResult, turnId}: FirstTurnInputProps) {
  if (turnCallsDataResult.firstTurnsLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <WaveLoader size="small" />
      </div>
    );
  }
  const input = turnCallsDataResult.firstTurnsInputs.get(turnId);
  if (!input) {
    return <span className="text-moon-500">{'No input'}</span>;
  }
  return <TruncatedJsonDisplay content={input} />;
}

interface LastTurnOutputProps {
  turnCallsDataResult: ReturnType<
    typeof useThreadsQuery
  >['turnCallsDataResult'];
  turnId: string;
}

function LastTurnOutput({turnCallsDataResult, turnId}: LastTurnOutputProps) {
  if (turnCallsDataResult.lastTurnsLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <WaveLoader size="small" />
      </div>
    );
  }
  const output = turnCallsDataResult.lastTurnsOutputs.get(turnId);
  if (!output) {
    return <span className="text-gray-500 italic">{'{No output}'}</span>;
  }
  return <TruncatedJsonDisplay content={output} />;
}
